import React, { useState, useEffect, useContext } from "react";
import { FolderIcon, ChevronRightIcon } from "@heroicons/react/24/solid";
import Preloader from "./LoadingAnimations/Preloader";
import { getFileIcon, formatFileSize } from "src/helpers/documentHelper";
import Breadcrumb from "./Settings/Breadcrumbs";
import { ArrowUpTrayIcon, HomeIcon, PencilSquareIcon, PlusIcon, TrashIcon } from "@heroicons/react/24/outline";
import DocumentPreviewModal from "../embedTypes/basic/Document/DocumentPreviewModal";
import { useParams } from "react-router-dom";
import useUrlQueryParams from "src/hooks/useUrlQueryParams";
import { apiRequest } from "src/async/apiUtils";
import PaginationFooter from "../Table/PaginationFooter";
import { toast } from "react-toastify";
import { formatDate } from "src/helpers/formatDate";
import Modal from "./Modal";
import { objectToFormData } from "src/helpers";
import Search from "../form/Search";
import Checkbox from "../form/Checkbox";
import ButtonDropdown from "../form/ButtonDropdown";
import DocumentUploadManager from "./DocumentUploadManager";
import InputGroup from "../form/Input";
import { AuthContext } from "src/context/auth";

const UploadedDocumentList = () => {
  const [limit, setLimit] = useState(10);
  const [offset, setOffset] = useState(0);
  const [meta, setMeta] = useState({});

  const [keyword, setKeyword] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [parentId, setParentId] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [loadingDocuments, setLoadingDocuments] = useState(false);

  const [selectedDocIndex, setSelectedDocIndex] = useState(-1);
  const [pathStack, setPathStack] = useState([{ id: null, icon: HomeIcon, label: "Root" }]);

  const [deleteModal, setDeleteModal] = useState({ isOpen: false, docId: null, isBulk: false });
  const [renameModal, setRenameModal] = useState({ isOpen: false, doc: null });
  const [newName, setNewName] = useState("");
  const [isProcessing, setIsProcessing] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);

  const [uploadType, setUploadType] = useState("file");
  const [uploadModalOpen, setUploadModalOpen] = useState(false);

  const { workspace_id, content_type, content_value } = useParams();
  const { group, page, form, document } = useUrlQueryParams();

  const { user } = useContext(AuthContext);

  const commonQueries = {
    group,
    page,
    form,
    document,
    ...(content_type && content_value ? { [content_type]: content_value } : {}),
  };

  useEffect(() => {
    const handler = setTimeout(() => setDebouncedSearch(keyword), 500);
    return () => clearTimeout(handler);
  }, [keyword]);

  const getUploadedDocuments = async () => {
    if (!user?._id) return;
    try {
      const queries = {
        parent_id: parentId,
        keyword: debouncedSearch,
        ...commonQueries,
      };
      setDocuments([]);
      setLoadingDocuments(true);
      const { data } = await apiRequest("get", "portal/workspaces/:workspace_id/documents", { params: { workspace_id }, queries });
      if (data.status === 200) {
        setDocuments(data?.data);
        setMeta(data?.meta || {});
      } else {
        setDocuments([]);
        setMeta(data?.meta || {});
      }
      setSelectedIds([]);
      setLoadingDocuments(false);
    } catch (error) {
      if (!debouncedSearch) {
        setLoadingDocuments(false);
      }
    }
  };

  useEffect(() => {
    getUploadedDocuments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [parentId, group, page, form, document, workspace_id, limit, offset, debouncedSearch]);

  const confirmDelete = async () => {
    setIsProcessing(true);
    try {
      if (deleteModal?.isBulk) {
        await apiRequest("delete", `portal/workspaces/:workspace_id/documents`, {
          queries: { ...commonQueries, ids: selectedIds },
          params: { workspace_id },
        });
      } else {
        await apiRequest("delete", `portal/workspaces/:workspace_id/documents/:document_id`, {
          queries: { ...commonQueries },
          params: { document_id: deleteModal.docId, workspace_id },
        });
      }

      getUploadedDocuments();
      setDeleteModal({ isOpen: false, docId: null, isBulk: false });
    } catch (error) {
      toast.error(error?.message || "Delete failed");
    } finally {
      setIsProcessing(false);
    }
  };

  const confirmRename = async () => {
    if (!newName || newName === renameModal.doc.name) {
      setRenameModal({ isOpen: false, doc: null });
      return;
    }
    setIsProcessing(true);
    try {
      await apiRequest(
        "put",
        `portal/workspaces/:workspace_id/documents/:document_id`,
        {
          body: objectToFormData({ name: newName }),
          queries: { ...commonQueries },
          params: { document_id: renameModal.doc?._id, workspace_id },
        },
        { headers: { "Content-Type": "multipart/form-data" } },
      );
      getUploadedDocuments();
      setRenameModal({ isOpen: false, doc: null });
    } catch (error) {
      toast.error(error?.message || "Rename failed");
    } finally {
      setIsProcessing(false);
    }
  };

  const handleSelectAll = () => {
    if (selectedIds.length === documents.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(documents.map((d) => d._id));
    }
  };

  const handleSelectOne = (id) => {
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]));
  };

  const handlePaginationChange = ({ limit, offset }) => {
    setLimit(limit);
    setOffset(offset);
  };

  const handleFolderClick = (doc) => {
    setPathStack((prev) => [...prev, { id: doc._id, icon: FolderIcon, label: doc.name }]);
    setParentId(doc._id);
    setOffset(0);
  };

  const handleBreadcrumbClick = (id, index) => {
    setPathStack((prev) => prev.slice(0, index + 1));
    setParentId(id);
    setOffset(0);
  };

  const handleOpen = (doc) => {
    setSelectedDocIndex(documents?.findIndex((document) => document?._id === doc?._id));
  };

  const handleOpenById = (id) => {
    const doc = documents.find((d) => d._id === id);
    if (doc) handleOpen(doc);
  };

  const handleAddUploadModalOpen = (type) => {
    setUploadType(type);
    setUploadModalOpen(true);
  };

  useEffect(() => {
    if (parentId === null) {
      setPathStack([{ id: null, icon: HomeIcon, label: "Root" }]);
    }
  }, [parentId]);

  const addDropdown = () => {
    return (
      <ButtonDropdown
        version="secondary"
        xPosition="right"
        dropdownClasses="!w-[300px]"
        className="!h-11 w-full sm:w-auto"
        dropdownOptions={[
          {
            title: "New folder",
            description: "Create an new folder to organize your files",
            icon: <PlusIcon className="h-5 w-5" />,
            onClick: () => handleAddUploadModalOpen("new_folder"),
          },
          {
            title: "Upload files",
            description: "Add a new file to your storage",
            icon: <ArrowUpTrayIcon className="h-5 w-5" />,
            onClick: () => handleAddUploadModalOpen("file"),
          },
          {
            title: "Upload folder",
            description: "Upload folder with files and nested subfolders",
            icon: <FolderIcon className="h-5 w-5" />,
            onClick: () => handleAddUploadModalOpen("folder"),
          },
        ]}>
        <PlusIcon className="mr-2 h-4 w-4 font-semibold" />
        Add Files / Folder
      </ButtonDropdown>
    );
  };

  return (
    <div className="animate-fade-in">
      {!user?._id ? (
        <div className="flex min-h-96 items-center justify-center rounded-lg border-2 border-dashed ">
          <div className="w-full sm:w-auto">{addDropdown()}</div>
        </div>
      ) : (
        <>
          <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="w-full sm:max-w-xs">
              <Search
                placeholder="Search documents..."
                iconClasses="!h-11 !w-11"
                inputClasses="!h-11 w-full"
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
              />
            </div>

            <div className="w-full sm:w-auto">{addDropdown()}</div>
          </div>
          <div className="scrollbar-hide flex items-center overflow-x-auto whitespace-nowrap px-3 pb-3">
            <Breadcrumb
              items={pathStack.map((item, index) => ({
                label: item.label,
                icon: item.icon,
                onClick: () => handleBreadcrumbClick(item.id, index),
              }))}
            />
          </div>

          {selectedIds.length > 0 && (
            <div className="animate-fade-in bg-highlightColor/10 border-highlightColor/20 mb-3 flex items-center justify-between rounded-lg border px-4 py-2">
              <span className="text-sm font-medium text-highlightColor">{selectedIds.length} items selected</span>
              <button
                onClick={() => setDeleteModal({ isOpen: true, docId: null, isBulk: true })}
                className="flex items-center gap-2 text-sm font-semibold text-red-600 hover:text-red-700">
                <TrashIcon className="h-4 w-4" />
                Delete
              </button>
            </div>
          )}

          <div className="flex min-h-[300px] flex-col overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
            {loadingDocuments ? (
              <div className="flex flex-grow items-center justify-center p-12">
                <Preloader
                  text="Loading documents..."
                  size="sm"
                  circleDimension="10"
                />
              </div>
            ) : (
              <>
                <div className="flex items-center border-b border-gray-100 bg-gray-50/50 px-4 py-3 text-xs font-semibold uppercase tracking-wider text-gray-500">
                  <div className="flex flex-1 items-center gap-4 p-1">
                    <Checkbox
                      isChecked={documents.length > 0 && selectedIds.length === documents.length}
                      onChange={handleSelectAll}
                    />
                    <span>Name</span>
                  </div>
                  <div className="flex gap-x-10">
                    <div className="hidden md:block">Date Created</div>
                    <div className="w-16">Actions</div>
                  </div>
                </div>

                <ul className="divide-y divide-gray-100">
                  {documents.length > 0 ? (
                    documents.map((doc) => {
                      const isFolder = doc.type === "folder";
                      const isChecked = selectedIds.includes(doc._id);

                      return (
                        <li
                          key={doc._id}
                          onClick={() => (isFolder ? handleFolderClick(doc) : handleOpenById(doc?._id))}
                          className={`flex cursor-pointer items-center justify-between p-3 transition hover:bg-gray-50 md:p-4 ${isFolder ? "group" : ""}`}>
                          <div className="flex min-w-0 flex-1 items-center space-x-3 p-1 md:space-x-4">
                            <div
                              onClick={(e) => e.stopPropagation()}
                              className="shrink-0">
                              <Checkbox
                                isChecked={isChecked}
                                onChange={() => handleSelectOne(doc._id)}
                              />
                            </div>

                            <div className={`shrink-0 rounded-lg p-2 ${isFolder ? "bg-amber-50" : "border border-gray-100 bg-gray-50"}`}>{isFolder ? <FolderIcon className="h-5 w-5 text-amber-500 md:h-6 md:w-6" /> : getFileIcon(doc.file?.extension)}</div>

                            <div className="min-w-0 flex-1">
                              <p className="truncate text-sm font-medium text-gray-900 group-hover:text-highlightColor">{doc.name}</p>
                              <div className="flex items-center gap-2 text-xs text-gray-500">
                                <span>{isFolder ? "Folder" : doc.file?.extension?.toUpperCase() || "FILE"}</span>
                                <span className="md:hidden">• {formatDate(doc?.created_at)}</span>
                                {!isFolder && <span className="hidden md:inline">• {formatFileSize(doc.file?.size_in_bytes)}</span>}
                              </div>
                            </div>
                          </div>

                          <div className="ml-2 flex shrink-0 items-center gap-x-2 md:gap-x-4">
                            <div className="hidden items-center gap-x-4 text-xs text-gray-400 md:flex">
                              {isFolder ? <ChevronRightIcon className="h-5 w-5 transition-transform group-hover:translate-x-1" /> : <span>{formatDate(doc?.created_at)}</span>}
                              <span className="text-gray-300">|</span>
                            </div>

                            <div className="flex items-center gap-x-1 md:gap-x-2">
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setNewName(doc.name);
                                  setRenameModal({ isOpen: true, doc });
                                }}
                                className="rounded p-1.5 text-gray-500 hover:bg-gray-200">
                                <PencilSquareIcon className="h-4 w-4" />
                              </button>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setDeleteModal({ isOpen: true, docId: doc._id, isBulk: false });
                                }}
                                className="rounded p-1.5 text-red-500 hover:bg-red-100">
                                <TrashIcon className="h-4 w-4" />
                              </button>
                            </div>
                          </div>
                        </li>
                      );
                    })
                  ) : (
                    <li className="p-20 text-center">
                      <div className="flex flex-col items-center">
                        <div className="mb-3 rounded-full bg-gray-50 p-4">
                          <FolderIcon className="h-10 w-10 text-gray-200" />
                        </div>
                        <p className="text-sm font-medium text-gray-500">No documents found here</p>
                        <p className="text-xs text-gray-400">Upload files to see them in this folder</p>
                      </div>
                    </li>
                  )}
                </ul>
              </>
            )}
          </div>

          <PaginationFooter
            itemName="Documents"
            limit={limit}
            offset={offset}
            count={meta?.count}
            onChange={handlePaginationChange}
          />
        </>
      )}

      <Modal
        title="Rename Item"
        size="xs"
        overlayClasses="!z-[80]"
        isOpen={renameModal.isOpen}
        isLoading={isProcessing}
        onCancel={() => setRenameModal({ isOpen: false, doc: null })}
        onSuccess={confirmRename}
        defaultOptions={{ onSuccessButtonText: "Rename" }}>
        <div className="py-4">
          <InputGroup
            inline
            name="name"
            label="Name"
            inputValue={newName}
            onChange={(e) => setNewName(e.target.value)}
          />
        </div>
      </Modal>

      <Modal
        title={deleteModal.isBulk ? "Delete Multiple Items" : "Delete Item"}
        size="xs"
        isOpen={deleteModal.isOpen}
        isLoading={isProcessing}
        onCancel={() => setDeleteModal({ isOpen: false, docId: null, isBulk: false })}
        onSuccess={confirmDelete}
        defaultOptions={{
          onSuccessButtonText: "Delete",
          customStyles: { color: "red" },
        }}>
        <div className="py-4">
          <p className="text-sm text-gray-600">{deleteModal.isBulk ? `Are you sure you want to delete ${selectedIds.length} items?` : "Are you sure you want to delete this item?"} This action cannot be undone.</p>
        </div>
      </Modal>

      <DocumentPreviewModal
        docs={documents?.filter((item) => item.type === "file")}
        initialIndex={selectedDocIndex}
        isOpen={selectedDocIndex !== -1 ? true : false}
        onClose={() => setSelectedDocIndex(-1)}
      />

      <DocumentUploadManager
        isOpen={uploadModalOpen}
        parentId={parentId}
        onClose={() => setUploadModalOpen(false)}
        uploadType={uploadType}
        refreshList={getUploadedDocuments}
      />
    </div>
  );
};

export default UploadedDocumentList;
